import React from "react";
import { map, sample } from "lodash-es";

import countryData from "../lib/countryData";
import { similarity } from "../lib/similarity";
import { useStoreState, useStoreActions } from "../store";

const countriesArray = map(countryData, (value, key) => {
  return { code: key, ...value };
});

function ResultBox({
  getCountryToGuess,
  guessSimilarity,
  guessText,
  countryToGuess
}) {
  const valentineMode = useStoreState(s => s.valentineMode);
  const buttonRef = React.useRef(null);

  React.useEffect(() => {
    buttonRef.current.focus();
  }, []);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        getCountryToGuess();
      }}
    >
      <div className="Game__result-text">
        {guessSimilarity === 1 ? (
          <>
            Correct! The country is <strong>{countryToGuess.name}</strong>.
            {valentineMode && (
              <>
                <br />
                Wow, You are so smart I think I'm in love... 💘
              </>
            )}
          </>
        ) : guessSimilarity > 0.75 ? (
          <>
            Correct. You made a typo though, it should be:{" "}
            <strong>{countryToGuess.name}</strong>
          </>
        ) : (
          <>
            You guessed {guessText} which is WRONG... <br />
            This country is: <strong>{countryToGuess.name}</strong>
          </>
        )}
      </div>
      <button
        ref={buttonRef}
        className="Game__button Game__button--next-large"
        type="submit"
      >
        Next
      </button>
    </form>
  );
}

function GameForm({ checkGuess, getCountryToGuess, setGuessText, guessText }) {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    inputRef.current.focus();
  }, []);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        checkGuess();
      }}
      style={{ display: "flex" }}
    >
      <button
        style={{ marginLeft: 0, marginRight: 10 }}
        className="Game__button Game__button--next"
        onClick={getCountryToGuess}
        type="button"
      >
        Next
      </button>

      <input
        className="Game__input"
        type="text"
        ref={inputRef}
        value={guessText}
        onChange={e => setGuessText(e.target.value)}
      />
      <button className="Game__button Game__button--guess" type="submit">
        Guess!
      </button>
    </form>
  );
}

export default function Game() {
  const [gameActive, setGameActive] = React.useState(false);
  const [countries, setCountries] = React.useState(countriesArray);
  const [guessText, setGuessText] = React.useState("");
  const [resultValue, setResultValue] = React.useState(null); // null, 0-1
  const [countryToGuess, setToGuess] = React.useState(null);
  const setFocusedCountry = useStoreActions(s => s.setFocusedCountry);

  const isShowingResults = resultValue !== null;

  React.useEffect(() => {
    if (!countryToGuess) return;
    setFocusedCountry(countryToGuess.code);
  }, [countryToGuess]);

  function startGame() {
    setGameActive(true);
    getCountryToGuess();
  }

  function endGame() {
    setGameActive(false);
    setCountries(countriesArray);
  }

  function getCountryToGuess() {
    const toGuess = sample(countries);
    setToGuess(toGuess);
    setCountries(countries.filter(c => c.code !== toGuess.code));
    setResultValue(null);
    setGuessText("");
  }

  function checkGuess() {
    const guessSimilarity = similarity(
      guessText.toLowerCase(),
      countryToGuess.name.toLowerCase()
    );

    setResultValue(guessSimilarity);
  }

  return gameActive ? (
    <div className="Game__container">
      {isShowingResults ? (
        <ResultBox
          getCountryToGuess={getCountryToGuess}
          guessSimilarity={resultValue}
          guessText={guessText}
          countryToGuess={countryToGuess}
        />
      ) : (
        <>
          <h4 className="Game__description">
            Guess what country is shown above!
          </h4>
          <div className="Game__hint">Hint: America is NOT a country</div>

          <GameForm
            setGuessText={setGuessText}
            getCountryToGuess={getCountryToGuess}
            checkGuess={checkGuess}
            guessText={guessText}
          />
        </>
      )}

      <div className="Game__buttons">
        <a
          className="Game__button Game__button--close"
          href="https://youtu.be/vnS-Dqf6P34?t=69"
          target="_blank"
          rel="noopener noreferrer"
        >
          I need a hint
        </a>

        <button className="Game__button Game__button--close" onClick={endGame}>
          Exit game
        </button>
      </div>
    </div>
  ) : (
    <button onClick={startGame} className="Game__start-btn">
      Let's play!
    </button>
  );
}
